import React from 'react';
import { motion } from 'framer-motion'; // For potential row animations

const ServerTable = ({ servers, loading, onStartTesting, onServerHover, onHoverEnd }) => {
  if (loading) {
    // Skeleton for table rows
    return (
      <div className="bg-dark-600 rounded-2xl overflow-hidden mb-8 border border-dark-400 animate-pulse">
        <div className="h-12 bg-dark-500"></div> {/* Header skeleton */}
        <div className="divide-y divide-dark-500">
          {[...Array(SERVERS_PER_PAGE || 6)].map((_, i) => (
            <div key={i} className="h-16 flex items-center px-4 py-3">
              <div className="w-8 h-8 bg-dark-500 rounded-lg mr-3"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-dark-500 rounded w-3/4"></div>
                <div className="h-3 bg-dark-500 rounded w-1/2"></div>
              </div>
              <div className="w-20 h-6 bg-dark-500 rounded ml-4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!servers || !servers.length) {
    return (
      <div className="text-center py-10 text-gray-400 bg-dark-600 rounded-2xl border border-dark-400 mb-8">
        <i className="fas fa-database text-4xl mb-4 animate-pulse-slow"></i>
        <p>No servers found matching your criteria.</p>
      </div>
    );
  }

  const getStatusClasses = (status) => {
      switch (status?.toLowerCase()) {
          case 'online': return 'bg-accent-green/20 text-accent-green';
          case 'offline': return 'bg-accent-red/20 text-accent-red';
          default: return 'bg-gray-500/20 text-gray-400';
      }
  };

  return (
    <div className="bg-dark-600 rounded-2xl overflow-hidden mb-8 border border-dark-400 hover:border-dark-300 transition-colors duration-300">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]"> {/* Min width for smaller screens */}
          <thead className="bg-dark-500">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Server</th>
              <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider hidden md:table-cell">Players</th>
              <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider hidden lg:table-cell">Version</th>
              <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider hidden sm:table-cell">Status</th>
              <th className="px-4 py-3 text-left font-medium text-gray-400 uppercase tracking-wider">Last Tested</th>
              <th className="px-4 py-3 text-right font-medium text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-500">
            {servers.map((server, index) => (
              <motion.tr
                key={server.ip || index} // Use a unique ID if available
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="hover:bg-dark-500/60 transition-colors group"
                onMouseEnter={() => onServerHover && onServerHover(server)}
                onMouseLeave={() => onHoverEnd && onHoverEnd()}
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center">
                    <img src={server.icon || `https://minotar.net/avatar/${server.ip}/32`} // Fallback avatar
                         alt=""
                         className="w-8 h-8 rounded-lg mr-3 pixelated flex-shrink-0 object-cover border border-dark-400 group-hover:scale-105 transition-transform"
                         onError={(e) => { e.target.onerror = null; e.target.src = 'https://minotar.net/avatar/steve/32'; }}/>
                    <div>
                      <div className="font-medium text-white group-hover:text-accent-blue transition-colors">{server.ip}</div>
                      <div className="text-xs text-gray-400 truncate max-w-[150px] group-hover:text-gray-300 transition-colors">{server.tags?.join(', ') || 'No tags'}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell text-gray-300 group-hover:text-white transition-colors">{server.players?.toLocaleString() ?? '?'}</td>
                <td className="px-4 py-3 whitespace-nowrap hidden lg:table-cell text-gray-300 group-hover:text-white transition-colors">{server.version || 'N/A'}</td>
                <td className="px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusClasses(server.status)}`}>
                    {server.status}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-400 group-hover:text-gray-300 transition-colors">{server.lastTested || 'Never'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <button
                    onClick={() => onStartTesting(server.ip)}
                    disabled={server.status !== 'online'}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition transform-gpu ${server.status === 'online'
                                ? 'bg-dark-500 hover:bg-gradient-to-r hover:from-accent-purple hover:to-accent-pink hover:text-white hover:-translate-y-0.5 active:translate-y-0'
                                : 'bg-dark-400 text-gray-500 cursor-not-allowed'
                             }`}
                  >
                    <i className="fas fa-terminal mr-1"></i> Test
                  </button>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Define default value if needed elsewhere
export const SERVERS_PER_PAGE = 9;

export default ServerTable;